import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from '../../../../ui/toast';
import {
  createJobTitle,
  deleteJobTitle,
} from '../../../../utils/api_service/job_title';
import {
  addJobTitleToRedux,
  deleteJobTitleFromRedux,
} from '../../../../utils/redux_store/features/job_title_reducer';
import { SelectOptions } from '../../../../utils/types/react_select_types';
import { ITEMS_PER_PAGE } from '../constants';
import { SortOption } from '../interfaces';

interface UseJobTitleManagementParams {
  jobTitles: SelectOptions[];
}

export const useJobTitleManagement = ({
  jobTitles = [],
}: UseJobTitleManagementParams) => {
  const dispatch = useDispatch();
  const [newJobTitle, setNewJobTitle] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [jobTitleToDelete, setJobTitleToDelete] =
    useState<SelectOptions | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortBy, setSortBy] = useState<SortOption>('name-asc');
  const itemsPerPage = ITEMS_PER_PAGE;

  const handleAddJobTitle = () => {
    if (!newJobTitle.trim()) {
      toast.error('Please enter a job title');
      return;
    }

    const title = newJobTitle.trim();
    createJobTitle(title)
      .then((response) => {
        if (response.id) {
          const newJob: SelectOptions = {
            value: response.id,
            label: response.jobTitle,
          };
          dispatch(addJobTitleToRedux(newJob));
          setNewJobTitle('');
          toast.success('Job title added successfully');
        }
      })
      .catch(() => {
        toast.error('Failed to add job title');
      });
  };

  const openDeleteModal = (jobTitle: SelectOptions) => {
    setJobTitleToDelete(jobTitle);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setJobTitleToDelete(null);
  };

  const handleDeleteJobTitle = () => {
    if (!jobTitleToDelete) return;

    setIsDeleting(true);
    deleteJobTitle(jobTitleToDelete.value)
      .then(() => {
        dispatch(deleteJobTitleFromRedux(jobTitleToDelete.value));
        toast.success('Job title deleted successfully');
        closeDeleteModal();
      })
      .catch(() => {
        toast.error('Failed to delete job title');
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const sortedAndFilteredJobTitles = useMemo(() => {
    let filtered = (jobTitles || []).filter((jobTitle) =>
      jobTitle.label.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    filtered.sort((a, b) => {
      if (sortBy === 'name-asc') {
        return a.label.localeCompare(b.label);
      } else if (sortBy === 'name-desc') {
        return b.label.localeCompare(a.label);
      }
      return 0;
    });

    return filtered;
  }, [jobTitles, searchQuery, sortBy]);

  const totalPages = Math.ceil(
    sortedAndFilteredJobTitles.length / itemsPerPage,
  );
  const paginatedJobTitles = sortedAndFilteredJobTitles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return {
    newJobTitle,
    setNewJobTitle,
    searchQuery,
    setSearchQuery: handleSearchChange,
    isDeleting,
    deleteModalOpen,
    jobTitleToDelete,
    currentPage,
    sortBy,
    setSortBy,
    itemsPerPage,
    sortedAndFilteredJobTitles,
    totalPages,
    paginatedJobTitles,
    handleAddJobTitle,
    handleDeleteJobTitle,
    openDeleteModal,
    closeDeleteModal,
    handlePageChange,
  };
};
